import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Mail, Lock, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const Login = () => {
  const { login, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (user) { navigate('/dashboard'); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email, password); navigate('/dashboard'); }
    catch (err) { setError(err.response?.data?.detail || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  return (
    <div className="flex" style={{ background: 'var(--ink)', minHeight: '100vh' }}>
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-12" data-testid="logo-link">
            <div className="w-7 h-7 bg-[var(--gold)] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)' }}>
              <span style={{ fontFamily: 'var(--display)', fontSize: 14, color: '#000', letterSpacing: 2 }}>C</span>
            </div>
            <span className="font-display text-2xl" style={{ color: 'var(--cream)', letterSpacing: 3 }}>CASTDECK</span>
          </Link>

          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--head)', color: 'var(--cream)' }}>Welcome Back</h1>
          <p className="text-sm mb-8" style={{ color: 'var(--warm)' }}>Sign in to access your casting dashboard</p>

          {error && (
            <div className="px-4 py-3 mb-6 text-sm" style={{ background: 'rgba(224,86,86,.1)', border: '1px solid rgba(224,86,86,.2)', color: 'var(--red)' }} data-testid="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-c)' }} />
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-11" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                  placeholder="Enter your email" required data-testid="email-input" />
              </div>
            </div>

            <div>
              <Label className="text-[10px] font-bold tracking-wider uppercase mb-2 block" style={{ color: 'var(--muted-c)', fontFamily: 'var(--head)' }}>Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-c)' }} />
                <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11" style={{ background: 'var(--surface)', borderColor: 'rgba(255,255,255,.08)', color: 'var(--cream)' }}
                  placeholder="Enter your password" required data-testid="password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-c)' }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn-gold w-full h-11 text-xs flex items-center justify-center" disabled={loading} data-testid="login-btn">
              {loading ? <Loader2 className="w-5 h-5 spinner" /> : 'SIGN IN'}
            </button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center"><div className="w-full" style={{ borderTop: '1px solid rgba(255,255,255,.06)' }} /></div>
            <div className="relative flex justify-center text-[10px] font-bold tracking-wider uppercase" style={{ fontFamily: 'var(--head)' }}>
              <span className="px-4" style={{ background: 'var(--ink)', color: 'var(--muted-c)' }}>Or continue with</span>
            </div>
          </div>

          <button type="button" onClick={googleLogin}
            className="btn-ghost w-full h-11 flex items-center justify-center gap-2 text-xs" data-testid="google-login-btn">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <p className="text-center mt-8 text-xs" style={{ color: 'var(--warm)' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: 'var(--gold)' }} data-testid="register-link">Create one</Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(to left, transparent 0%, var(--ink) 100%)' }} />
        <img src="https://images.unsplash.com/photo-1576987212553-c43b8e44bb1f?w=1200&q=80" alt="Casting" className="w-full h-full object-cover" />
        <div className="absolute bottom-12 left-12 right-12 z-20">
          <blockquote className="text-base italic" style={{ color: 'rgba(245,242,255,.9)', fontFamily: 'var(--serif)' }}>
            "CastDeck transformed how we discover and book talent. What used to take weeks now happens in hours."
          </blockquote>
          <div className="mt-4">
            <div className="text-sm font-bold" style={{ color: 'var(--cream)', fontFamily: 'var(--head)' }}>Priya Sharma</div>
            <div className="text-xs" style={{ color: 'var(--muted-c)' }}>Casting Director, Dharma Productions</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
